/**
 * @fileoverview "Tasks tracker" tab of the project view: the same matrix,
 * drawers and "Upload for class" flow as the standalone Task Tracker page,
 * scoped to the project being viewed with a student filter.
 */

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Select } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { Stack } from 'components'
import { useArcTranslation } from 'hooks'
import { notifications, useCurrentUser } from 'context'
import {
  TaskTrackerEmptyState,
  TaskTrackerLoadingState,
  TaskTrackerSummary,
  TaskTrackerTable,
} from '../../../TaskTracker/components'
import { TaskTrackerOverlays } from '../../../TaskTracker/components/TaskTrackerOverlays'
import { useTaskTrackerDrawers } from '../../../TaskTracker/hooks/useTaskTrackerDrawers'
import { isProjectCompleted } from '../../../TaskTracker/projectStatus'
import { countTeacherUploads } from '../../../TaskTracker/service/taskTrackerService'
import { ALL_STUDENTS, useProjectMatrix } from './useProjectMatrix'

const SELECTOR_WIDTH = 320

interface TasksTrackerTabProps {
  projectName: string
  projectStatus: string
}

export const TasksTrackerTab = ({ projectName, projectStatus }: TasksTrackerTabProps) => {
  const { id: projectId } = useParams()
  const { t } = useArcTranslation()
  const { currentUser } = useCurrentUser()
  const [selectedStudentId, setSelectedStudentId] = useState<string>(ALL_STUDENTS)

  const showLoadError = () => {
    notifications.show({ message: t('task_tracker.error.load_failed'), color: 'red', icon: <IconX size={18} /> })
  }
  const { matrix, loading, reload, reloadSilent } = useProjectMatrix({
    projectId, selectedStudentId, onLoadError: showLoadError,
  })
  const projectFinished = isProjectCompleted(projectStatus)
  const drawers = useTaskTrackerDrawers({ matrix, projectId: projectId ?? '', projectName })

  const allStudentsOption = { value: ALL_STUDENTS, label: t('arc_tasks_tracker.all_students') }
  const studentOptions = [
    allStudentsOption,
    ...(matrix?.students ?? []).map(s => ({ value: s.id, label: s.fullName })),
  ]

  const hasTasks = Boolean(matrix && (matrix.activities.length > 0 || matrix.finalAssessment.present))
  const noTasks = Boolean(matrix) && !hasTasks
  const noStudents = Boolean(matrix) && hasTasks && matrix?.students.length === 0
  const showTable = !loading && matrix && !noTasks && !noStudents

  return (
    <Stack spacing='lg'>
      <Select
        label={t('arc_tasks_tracker.student_label')}
        placeholder={t('arc_tasks_tracker.select_student_placeholder')}
        data={studentOptions}
        value={selectedStudentId}
        onChange={next => setSelectedStudentId(next ?? ALL_STUDENTS)}
        disabled={!matrix || matrix.students.length === 0}
        searchable
        nothingFound={t('arc_tasks_tracker.no_student_match')}
        style={{ width: SELECTOR_WIDTH }}
      />

      {matrix && !noTasks && !noStudents && (
        <TaskTrackerSummary summary={matrix.summary} uploadedByTeacherCount={countTeacherUploads(matrix)} />
      )}

      {loading && <TaskTrackerLoadingState />}
      {!loading && matrix && noTasks && <TaskTrackerEmptyState variant='no_tasks' />}
      {!loading && matrix && noStudents && <TaskTrackerEmptyState variant='no_students' />}

      {showTable && (
        <TaskTrackerTable
          activities={matrix.activities}
          students={matrix.students}
          onTeacherAssessmentClick={drawers.openAssessment}
          onDeliverableCellClick={drawers.openDeliverable}
          onFinalAssessmentClick={drawers.openFinal}
          onUploadForClassClick={projectFinished ? undefined : drawers.openUploadForClass}
          currentUserId={currentUser?.id}
        />
      )}

      <TaskTrackerOverlays
        drawers={drawers}
        projectId={projectId ?? ''}
        projectName={projectName}
        projectFinished={projectFinished}
        onRefresh={reload}
        onRefreshSilent={reloadSilent}
      />
    </Stack>
  )
}
